import register from 'preact-custom-element'
import { useEffect, useState } from 'preact/hooks'
import { h } from 'preact'

declare global {
  interface Window {
    Handlebars: any
    SCASLSetting?: any
  }
}

interface SelectComponentProps {
  id?: string
  idContainer?: string
  name?: string
  className?: string
  classContainer?: string
  settingKey: string
  defaultSettingKey?: string
  settingKeyAppend?: string
  additionalOption?: {
    value: string | number
    text: string
  }
}

interface SCASLSetting {
  radius?: number[]
  df_radius?: number
  df_measurement?: string
  selected_tags?: string[]
  [key: string]: any
}

export class ScaStoreLocatorElement extends HTMLElement {
  private static handlebarsIntercepted = false
  private static accordionInitialized = false
  private static mutationObserver: MutationObserver | null = null
  private static processedHeaders: WeakSet<Element> = new WeakSet()

  private listContainer: HTMLUListElement | null = null

  private static initPromapVariables() {
    if (typeof window === 'undefined') return
    ;(window as any).arrowUpImg = undefined
    ;(window as any).arrowDownImg = 'https://'
  }

  private static initHandlebars() {
    if (ScaStoreLocatorElement.handlebarsIntercepted || typeof window === 'undefined') {
      return
    }

    ScaStoreLocatorElement.handlebarsIntercepted = true

    const obj = window
    const prop = 'Handlebars'
    let origValue = obj[prop]

    Object.defineProperty(obj, prop, {
      get() {
        return origValue
      },
      set(val: any) {
        registerHandlebarsHelpers(val)
        origValue = val
        return origValue
      }
    })

    if (origValue) {
      registerHandlebarsHelpers(origValue)
    }
  }

  private setupAccordionHandlers(container: Document | Element) {
    const headers = container.querySelectorAll<HTMLElement>('.scasl-accordion-header')

    headers.forEach((header) => {
      if (ScaStoreLocatorElement.processedHeaders.has(header)) {
        return
      }

      ScaStoreLocatorElement.processedHeaders.add(header)

      header.addEventListener('click', () => {
        const targetModalId = header.getAttribute('data-modal-parent')
        if (!targetModalId) return

        setTimeout(() => {
          const modals = document.querySelectorAll<HTMLElement>(
            'x-modal[data-id^="accordion-store-locator-"]'
          )

          modals.forEach((modal) => {
            const modalId = modal.getAttribute('data-id')
            const isVisible = modal.getAttribute('data-ui') === 'visible'

            if (modalId !== targetModalId && isVisible) {
              const modalElement = modal as any
              if (typeof modalElement.handleHide === 'function') {
                modalElement.handleHide()
              }
            }
          })
        }, 50)
      })
    })
  }

  private initAccordionObserver() {
    if (ScaStoreLocatorElement.accordionInitialized) {
      return
    }

    ScaStoreLocatorElement.accordionInitialized = true

    ScaStoreLocatorElement.mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              this.setupAccordionHandlers(node as Element)
            }
          })
        }
      })
    })

    this.listContainer = this.querySelector('[data-store-list] ul') ||
                         this.querySelector('#scasl-list ul')

    if (this.listContainer) {
      ScaStoreLocatorElement.mutationObserver.observe(this.listContainer, {
        childList: true,
        subtree: true
      })
    }

    this.setupAccordionHandlers(this)
  }

  connectedCallback() {
    ScaStoreLocatorElement.initPromapVariables()
    ScaStoreLocatorElement.initHandlebars()

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initAccordionObserver(), {
        once: true
      })
    } else {
      this.initAccordionObserver()
    }

    window.addEventListener('load', () => this.initAccordionObserver(), { once: true })
  }

  disconnectedCallback() {
  }
}

function registerHandlebarsHelpers(Handlebars: any) {
  if (!Handlebars) return

  Handlebars.registerHelper('formatOperatingHour', function (operatingHour: string) {
    if (!operatingHour) return ''
    return operatingHour.replace(
      /(\d{1,2}:\d{2}[ap]m)-(\d{1,2}:\d{2}[ap]m)/i,
      (_match: string, start: string, end: string) =>
        `${start.toLowerCase()} - ${end.toLowerCase()}`
    )
  })

  Handlebars.registerHelper('formatPhoneNumber', (phone: string) => phone || '')

  Handlebars.registerHelper('formatDistance', (distance: number) =>
    `${Math.round(distance)}kms away`
  )

  Handlebars.registerHelper('when', function (
    this: any,
    operand1: any,
    operator: string,
    operand2: any,
    options: any
  ) {
    const operators: Record<string, (l: any, r: any) => boolean> = {
      eq: (l, r) => l === r,
      noteq: (l, r) => l !== r,
      gt: (l, r) => +l > +r,
      gteq: (l, r) => +l > +r || l === r,
      lt: (l, r) => +l < +r,
      lteq: (l, r) => +l < +r || l === r,
      or: (l, r) => l || r,
      and: (l, r) => l && r,
      '%': (l, r) => l % r === 0
    }

    const result = operators[operator]?.(operand1, operand2)
    return result ? options.fn(this) : options.inverse(this)
  })
}

function useAsyncSettingLoading() {
  const [loadedSetting, setLoadedSetting] = useState<SCASLSetting | null>(null)

  useEffect(() => {
    const loadSettingAsync = async () => {
      await new Promise((resolve) => setTimeout(resolve, 2000))
      setLoadedSetting(window.SCASLSetting)
    }

    loadSettingAsync()
  }, [])

  return loadedSetting
}

function SelectComponent({
  id,
  idContainer,
  name,
  className,
  classContainer,
  settingKey,
  defaultSettingKey,
  settingKeyAppend,
  additionalOption
}: SelectComponentProps) {
  const loadedSetting = useAsyncSettingLoading()

  if (!loadedSetting || !loadedSetting[settingKey]) {
    return null
  }

  const options = loadedSetting[settingKey].map((value: any, index: number) =>
    h('option', { key: index, value }, `${value} ${loadedSetting[settingKeyAppend || '']}`)
  )

  if (additionalOption) {
    options.unshift(
      h('option', { value: additionalOption.value }, additionalOption.text)
    )
  }

  return h(
    'div',
    { id: idContainer, class: classContainer },
    h('select', {
      id,
      name,
      class: className,
      value: loadedSetting[defaultSettingKey || '']
    }, ...options)
  )
}

function ScaStorelocatorRadius() {
  const additionalOption = {
    value: 0,
    text: 'No Limit'
  }

  return h(SelectComponent, {
    id: 'scasl-radius-container',
    name: 'bh-sl-maxdistance',
    className: 'input',
    settingKey: 'radius',
    defaultSettingKey: 'df_radius',
    settingKeyAppend: 'df_measurement',
    additionalOption
  })
}

function ScaStorelocatorTagList() {
  const additionalOption = {
    value: '',
    text: 'All'
  }

  return h(SelectComponent, {
    settingKey: 'selected_tags',
    className: 'input',
    idContainer: 'scasl-tag-list-container',
    classContainer: 'bh-sl-filters scasl-tag-list [&>li]:hidden',
    additionalOption
  })
}

register(ScaStorelocatorRadius, 'sca-storelocator-radius')
register(ScaStorelocatorTagList, 'sca-storelocator-tag-list')
